/**
 * Material Detail Page
 *
 * Displays detailed material information including:
 * - Material header info
 * - Price history
 * - Where-used analysis (which BOMs use this material)
 * - Add price functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Loader2,
  Boxes,
  DollarSign,
  Layers,
  AlertCircle,
  Plus,
  TrendingUp,
  TrendingDown,
  History,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import materialService from '../services/materialService';
import bomService from '../services/bomService';

function MaterialDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [material, setMaterial] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [whereUsed, setWhereUsed] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'info');

  // Add price dialog state
  const [showAddPriceDialog, setShowAddPriceDialog] = useState(false);
  const [newPrice, setNewPrice] = useState({
    unit_price_tax_included: '',
    tax_rate: '0.13',
    supplier_name: '',
    notes: '',
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // Load user info
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load material data
  const loadMaterial = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const materialData = await materialService.getMaterial(id);
      setMaterial(materialData);
      setPriceHistory(materialData.price_history || []);

      // Load where-used
      try {
        const whereUsedData = await bomService.getWhereUsed(id);
        setWhereUsed(whereUsedData.used_in || []);
      } catch (err) {
        console.error('Failed to load where-used:', err);
      }
    } catch (err) {
      console.error('Failed to load material:', err);
      setError('加载物料详情失败，请重试。');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMaterial();
  }, [loadMaterial]);

  const handleBack = () => {
    navigate('/materials');
  };

  const handleEdit = () => {
    navigate(`/materials/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要删除此物料吗？')) {
      return;
    }

    try {
      setLoading(true);
      await materialService.deleteMaterial(id);
      navigate('/materials');
    } catch (err) {
      setError(`删除物料失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrice = async () => {
    if (!newPrice.unit_price_tax_included) {
      setError('请输入价格');
      return;
    }

    try {
      setSavingPrice(true);
      await materialService.addMaterialPrice(id, {
        unit_price_tax_included: parseFloat(newPrice.unit_price_tax_included),
        tax_rate: parseFloat(newPrice.tax_rate),
        supplier_name: newPrice.supplier_name,
        notes: newPrice.notes,
      });

      setShowAddPriceDialog(false);
      setNewPrice({
        unit_price_tax_included: '',
        tax_rate: '0.13',
        supplier_name: '',
        notes: '',
      });

      // Reload material to get updated prices
      loadMaterial();
    } catch (err) {
      setError(`添加价格失败: ${err.message}`);
    } finally {
      setSavingPrice(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return `¥${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  const getCategoryBadge = (category) => {
    const variants = {
      '棒料': 'default',
      '紧固件': 'secondary',
      '耗材': 'outline',
    };
    return (
      <Badge variant={variants[category] || 'outline'}>
        {category || '其他'}
      </Badge>
    );
  };

  // Calculate price change
  const getPriceChange = () => {
    if (priceHistory.length < 2) return null;
    const currentPrice = priceHistory[0]?.unit_price_tax_included || 0;
    const previousPrice = priceHistory[1]?.unit_price_tax_included || 0;
    if (previousPrice === 0) return null;
    const change = ((currentPrice - previousPrice) / previousPrice) * 100;
    return {
      percentage: change.toFixed(1),
      isIncrease: change > 0,
    };
  };

  // Check permissions
  const canEdit = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);
  const priceChange = getPriceChange();

  if (loading && !material) {
    return (
      <Layout activeTab="materials">
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !material) {
    return (
      <Layout activeTab="materials">
        <div className="container mx-auto py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回物料列表
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="materials">
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{material?.product_id}</h1>
                {material && getCategoryBadge(material.material_category)}
                {material?.is_active !== false ? (
                  <Badge variant="default">有效</Badge>
                ) : (
                  <Badge variant="destructive">已停用</Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                {material?.name}
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </Button>
              <Button
                variant="outline"
                className="text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                删除
              </Button>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="info">
              <Boxes className="h-4 w-4 mr-2" />
              基本信息
            </TabsTrigger>
            <TabsTrigger value="prices">
              <DollarSign className="h-4 w-4 mr-2" />
              价格历史
            </TabsTrigger>
            <TabsTrigger value="where-used">
              <Layers className="h-4 w-4 mr-2" />
              使用情况
            </TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Material Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Boxes className="h-5 w-5" />
                    物料信息
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">物料编号</Label>
                      <p className="font-mono">{material?.product_id}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">物料名称</Label>
                      <p>{material?.name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">类别</Label>
                      <p>{material?.material_category || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">单位</Label>
                      <p>{material?.unit || '个'}</p>
                    </div>
                    {material?.diameter && (
                      <div>
                        <Label className="text-muted-foreground">直径</Label>
                        <p>{material.diameter}mm</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">规格</Label>
                      <p>{material?.specification || '-'}</p>
                    </div>
                  </div>
                  {material?.notes && (
                    <div>
                      <Label className="text-muted-foreground">备注</Label>
                      <p>{material.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Price Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    当前价格
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {material?.current_price
                        ? formatPrice(material.current_price.unit_price_tax_included)
                        : formatPrice(material?.default_unit_cost)}
                    </span>
                    {priceChange && (
                      <span className={`flex items-center text-sm ${
                        priceChange.isIncrease ? 'text-red-500' : 'text-green-500'
                      }`}>
                        {priceChange.isIncrease ? (
                          <TrendingUp className="h-4 w-4 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 mr-1" />
                        )}
                        {priceChange.isIncrease ? '+' : ''}{priceChange.percentage}%
                      </span>
                    )}
                  </div>
                  {material?.current_price && (
                    <div className="text-sm text-muted-foreground">
                      <p>供应商: {material.current_price.supplier_name || '-'}</p>
                      <p>生效日期: {formatDate(material.current_price.effective_date)}</p>
                      <p>税率: {(material.current_price.tax_rate * 100).toFixed(0)}%</p>
                    </div>
                  )}
                  {canEdit && (
                    <Button onClick={() => setShowAddPriceDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      添加新价格
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Price History Tab */}
          <TabsContent value="prices" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    价格历史
                  </CardTitle>
                  {canEdit && (
                    <Button size="sm" onClick={() => setShowAddPriceDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      添加价格
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {priceHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    暂无价格记录
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>生效日期</TableHead>
                        <TableHead>含税价格</TableHead>
                        <TableHead>税率</TableHead>
                        <TableHead>不含税价格</TableHead>
                        <TableHead>供应商</TableHead>
                        <TableHead>来源</TableHead>
                        <TableHead>状态</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.map((price, index) => (
                        <TableRow key={price._id}>
                          <TableCell>{formatDate(price.effective_date)}</TableCell>
                          <TableCell className="font-medium">
                            {formatPrice(price.unit_price_tax_included)}
                          </TableCell>
                          <TableCell>
                            {((price.tax_rate || 0) * 100).toFixed(0)}%
                          </TableCell>
                          <TableCell>
                            {formatPrice(price.unit_price_tax_excluded)}
                          </TableCell>
                          <TableCell>{price.supplier_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {price.source === 'manual' ? '手动' :
                               price.source === 'ai_extraction' ? 'AI提取' :
                               price.source === 'migrated_from_bar_stock' ? '数据迁移' :
                               price.source || '未知'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {price.is_active ? (
                              <Badge variant="default">当前</Badge>
                            ) : (
                              <Badge variant="secondary">历史</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Where Used Tab */}
          <TabsContent value="where-used" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  使用情况 ({whereUsed.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {whereUsed.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    此物料尚未被任何BOM使用
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>BOM编号</TableHead>
                        <TableHead>成品</TableHead>
                        <TableHead>版本</TableHead>
                        <TableHead>单耗</TableHead>
                        <TableHead>损耗率</TableHead>
                        <TableHead>默认</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {whereUsed.map((usage) => (
                        <TableRow
                          key={usage.bom_object_id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/bom/${usage.bom_object_id}`)}
                        >
                          <TableCell className="font-mono">{usage.bom_id}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{usage.finished_product_code}</div>
                              <div className="text-sm text-muted-foreground">
                                {usage.finished_product_name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>V{usage.version}</TableCell>
                          <TableCell>
                            {usage.quantity_per} {usage.unit}
                          </TableCell>
                          <TableCell>
                            {((usage.scrap_rate || 0) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell>
                            {usage.is_default && (
                              <Badge variant="outline">默认</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Price Dialog */}
        <Dialog open={showAddPriceDialog} onOpenChange={setShowAddPriceDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>添加新价格</DialogTitle>
              <DialogDescription>
                为 {material?.name} 添加新的价格记录
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">含税价格 *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newPrice.unit_price_tax_included}
                    onChange={(e) => setNewPrice({
                      ...newPrice,
                      unit_price_tax_included: e.target.value
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_rate">税率</Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    step="0.01"
                    placeholder="0.13"
                    value={newPrice.tax_rate}
                    onChange={(e) => setNewPrice({
                      ...newPrice,
                      tax_rate: e.target.value
                    })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier">供应商</Label>
                <Input
                  id="supplier"
                  placeholder="供应商名称"
                  value={newPrice.supplier_name}
                  onChange={(e) => setNewPrice({
                    ...newPrice,
                    supplier_name: e.target.value
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">备注</Label>
                <Input
                  id="notes"
                  placeholder="可选备注"
                  value={newPrice.notes}
                  onChange={(e) => setNewPrice({
                    ...newPrice,
                    notes: e.target.value
                  })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAddPriceDialog(false)}
              >
                取消
              </Button>
              <Button onClick={handleAddPrice} disabled={savingPrice}>
                {savingPrice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                保存
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default MaterialDetailPage;
